import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, Field, PageHeader, Pager, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Pick } from "@/pages/gerencia/Sales";
import { EmployeeAvatar } from "@/components/shared/EmployeeAvatar";
import { EmployeeImportDialog } from "./EmployeeImportDialog";

export const useCatalog = (code) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/catalogs/${code}`).then((r) => setItems(r.data.map((i) => ({ id: i.code, name: i.name })))).catch(() => {}); }, [code]);
  return items;
};

export const emptyEmp = { rut: "", nombres: "", apellidos: "", birth_date: null, nacionalidad: "Chilena", estado_civil: "", address: "", phone: "", email: "", position: "", area: "", worker_type: "", contract_type: "", hire_date: "", cost_center_id: null, supervisor_id: null };

const CIVIL = ["Soltero(a)", "Casado(a)", "Conviviente civil", "Separado(a)", "Divorciado(a)", "Viudo(a)"].map((x) => ({ id: x, name: x }));

export const EmployeeForm = ({ value, onChange, disabled }) => {
  const cargos = useCatalog("cargos"), areas = useCatalog("areas"), tipos = useCatalog("tipos_trabajador"), contratos = useCatalog("tipos_contrato");
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const txt = (k, l, type = "text") => <Field label={l}><Input data-testid={`emp-${k}-input`} type={type} disabled={disabled} value={value[k] || ""} onChange={(e) => set(k)(e.target.value || (type === "date" ? null : ""))} /></Field>;
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {txt("rut", "RUT *")}{txt("nombres", "Nombres *")}{txt("apellidos", "Apellidos *")}{txt("birth_date", "Fecha de nacimiento", "date")}
      {txt("nacionalidad", "Nacionalidad")}
      <Field label="Estado civil"><Pick testid="emp-estado-civil-select" disabled={disabled} value={value.estado_civil} onChange={set("estado_civil")} options={CIVIL} placeholder="Seleccione" /></Field>
      {txt("phone", "Teléfono")}{txt("email", "Email")}
      <div className="sm:col-span-2">{txt("address", "Dirección")}</div>
      <Field label="Cargo *" hint={cargos.length === 0 ? "Cree los cargos en Administración → Maestros → Cargos." : ""}><Pick testid="emp-position-select" disabled={disabled} value={value.position} onChange={set("position")} options={cargos} placeholder="Seleccione" /></Field>
      <Field label="Área *"><Pick testid="emp-area-select" disabled={disabled} value={value.area} onChange={set("area")} options={areas} placeholder="Seleccione" /></Field>
      <Field label="Tipo de trabajador"><Pick testid="emp-worker-type-select" disabled={disabled} value={value.worker_type} onChange={set("worker_type")} options={tipos} placeholder="Seleccione" /></Field>
      <Field label="Tipo de contrato"><Pick testid="emp-contract-type-select" disabled={disabled} value={value.contract_type} onChange={set("contract_type")} options={contratos} placeholder="Seleccione" /></Field>
      {txt("hire_date", "Fecha de ingreso *", "date")}
    </div>
  );
};

export default function Employees() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("activo");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 20 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyEmp);
  const [importing, setImporting] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => { api.get("/rrhh/employees", { params: { q, status, page } }).then((r) => setData(r.data)); }, [q, status, page, reload]);
  const create = async (e) => {
    e.preventDefault();
    try { const { data: emp } = await api.post("/rrhh/employees", form); toast.success(`Trabajador ${emp.internal_id} creado`); navigate(`/rrhh/empleados/${emp.id}`); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div data-testid="employees-page">
      <PageHeader eyebrow="RR.HH." title="Empleados" subtitle="Cada trabajador existe una sola vez y su expediente se reutiliza en todas las OT."
        actions={can("rrhh.empleados.crear") && <>
          {can("rrhh.empleados.editar") && can("rrhh.empleados.crear") && <Button data-testid="import-employees-btn" variant="outline" className="rounded-full" onClick={() => setImporting(true)}><Upload size={16} className="mr-1" />Importar planilla</Button>}
          <Button data-testid="create-employee-btn" className="rounded-full" onClick={() => setOpen(true)}><Plus size={16} className="mr-1" />Nuevo trabajador</Button>
        </>} />
      <EmployeeImportDialog open={importing} onOpenChange={setImporting} onDone={() => { setImporting(false); setReload((x) => x + 1); }} />
      <div className="syc-card overflow-hidden">
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-[#e4e8e1]">
          <div className="relative flex-1"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a9584]" />
            <Input data-testid="employees-search-input" className="pl-9" placeholder="Buscar por nombre, RUT o ID" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} /></div>
          <div className="flex gap-2">{["activo", "inactivo"].map((s) => <Button key={s} data-testid={`employees-filter-${s}`} variant={status === s ? "default" : "outline"} className="rounded-full" onClick={() => { setStatus(s); setPage(1); }}>{s === "activo" ? "Activos" : "Inactivos"}</Button>)}</div>
        </div>
        {data.items.length === 0 ? <EmptyState title="Sin trabajadores" /> : data.items.map((e) => (
          <Link key={e.id} to={`/rrhh/empleados/${e.id}`} data-testid={`employee-row-${e.id}`} className="flex items-center gap-4 px-5 py-3 border-b border-[#f0f2ee] hover:bg-[#f6fbf2] text-sm">
            <EmployeeAvatar id={e.id} v={e.photo_v} name={e.full_name} size={36} />
            <span className="font-mono text-xs w-24 hidden sm:block">{e.internal_id}</span>
            <div className="flex-1 min-w-0"><div className="font-semibold truncate">{e.full_name}</div><div className="text-xs text-[#5b6656]">{e.rut} · {e.position} · {e.area}</div></div>
            <StatusBadge status={e.status} label={e.status === "activo" ? "Activo" : "Inactivo"} />
          </Link>
        ))}
        <Pager page={page} total={data.total} pageSize={data.page_size} onPage={setPage} />
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-employee-dialog">
          <DialogHeader><DialogTitle>Nuevo trabajador</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4"><EmployeeForm value={form} onChange={setForm} />
            <DialogFooter><Button data-testid="create-employee-submit-btn" className="rounded-full">Crear trabajador</Button></DialogFooter></form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
